import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Grid,
  Info,
} from "lucide-react";

const SetMatrixZeroesVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "3x3 Simple Case",
      matrix: [
        [1, 1, 1],
        [1, 0, 1],
        [1, 1, 1],
      ],
    },
    {
      id: 2,
      name: "4x4 Multiple Zeroes",
      matrix: [
        [0, 1, 2, 0],
        [3, 4, 5, 2],
        [1, 3, 1, 5],
      ],
    },
    {
      id: 3,
      name: "No Zeroes",
      matrix: [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
      ],
    },
    {
      id: 4,
      name: "Zero in First Row/Col",
      matrix: [
        [1, 1, 0],
        [1, 1, 1],
        [1, 1, 1],
      ],
    },
    {
      id: 5,
      name: "All Zeroes",
      matrix: [
        [0, 0],
        [0, 0],
      ],
    },
    {
      id: 6,
      name: "Long Matrix",
      matrix: [
        [1, 2, 3, 4, 5, 0],
        [6, 7, 8, 9, 1, 2],
        [0, 3, 4, 5, 6, 7],
      ],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [matrix, setMatrix] = useState(defaultTestCases[0].matrix);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function setZeroes(matrix):", indent: 0 },
    { line: "R = matrix.length, C = matrix[0].length", indent: 1 },
    { line: "isCol0 = false", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Step 1: Mark first row/col based on zeroes", indent: 1 },
    { line: "for r = 0 to R-1:", indent: 1 },
    { line: "if matrix[r][0] == 0: isCol0 = true", indent: 2 },
    { line: "for c = 1 to C-1:", indent: 2 },
    { line: "if matrix[r][c] == 0:", indent: 3 },
    { line: "matrix[r][0] = 0", indent: 4 },
    { line: "matrix[0][c] = 0", indent: 4 },
    { line: "", indent: 0 },
    { line: "// Step 2: Set zeroes based on marks", indent: 1 },
    { line: "for r = 1 to R-1:", indent: 1 },
    { line: "for c = 1 to C-1:", indent: 2 },
    { line: "if matrix[r][0] == 0 or matrix[0][c] == 0:", indent: 3 },
    { line: "matrix[r][c] = 0", indent: 4 },
    { line: "", indent: 0 },
    { line: "// Step 3: Set first row/col if needed", indent: 1 },
    { line: "if matrix[0][0] == 0:", indent: 1 },
    { line: "for c = 0 to C-1: matrix[0][c] = 0", indent: 2 },
    { line: "if isCol0:", indent: 1 },
    { line: "for r = 0 to R-1: matrix[r][0] = 0", indent: 2 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps for the optimal approach
  const calculateSteps = (inputMatrix) => {
    const R = inputMatrix.length;
    if (R === 0) return [];
    const C = inputMatrix[0].length;
    let matrix = JSON.parse(JSON.stringify(inputMatrix));
    const allSteps = [];
    let stepCount = 0;

    const addStep = (
      description,
      highlightLine,
      pointers = {},
      changes = []
    ) => {
      allSteps.push({
        step: stepCount++,
        matrix: JSON.parse(JSON.stringify(matrix)),
        description,
        highlightLine,
        pointers,
        changes,
      });
    };

    addStep("Initial matrix.", 0);

    let isCol0 = false;
    // Step 1: Mark
    addStep("Step 1: Find zeroes and mark the first row & column.", 4);
    for (let r = 0; r < R; r++) {
      addStep(`Checking first element of row ${r}.`, 6, { r, c: 0 });
      if (matrix[r][0] === 0) {
        isCol0 = true;
        addStep(
          `Found a zero at [${r}][0]. Mark first column to be zeroed.`,
          6,
          { r, c: 0 },
          [{ r, c: 0, marked: "col" }]
        );
      }
      for (let c = 1; c < C; c++) {
        addStep(`Checking element at [${r}][${c}].`, 8, { r, c });
        if (matrix[r][c] === 0) {
          addStep(
            `Found a zero at [${r}][${c}]. Mark row ${r} and column ${c}.`,
            9,
            { r, c },
            [
              { r, c: 0, marked: "row" },
              { r: 0, c, marked: "col" },
            ]
          );
          matrix[r][0] = 0;
          matrix[0][c] = 0;
          addStep(`Marks set in first row and column.`, 10, { r, c: 0 });
        }
      }
    }

    // Step 2: Set inner matrix
    addStep("Step 2: Use marks to set zeroes in the inner matrix.", 13);
    for (let r = 1; r < R; r++) {
      for (let c = 1; c < C; c++) {
        addStep(`Checking marks for cell [${r}][${c}].`, 15, { r, c });
        if (matrix[r][0] === 0 || matrix[0][c] === 0) {
          addStep(
            `Mark found. Setting [${r}][${c}] to 0.`,
            16,
            { r, c, r_mark: r, c_mark: c },
            [{ r, c, value: 0 }]
          );
          matrix[r][c] = 0;
        }
      }
    }

    // Step 3: Set first row and col
    addStep("Step 3: Set first row and column based on their marks.", 19);
    if (matrix[0][0] === 0) {
      addStep("First element is 0, setting entire first row to 0.", 20, {
        r: 0,
      });
      for (let c = 0; c < C; c++) {
        matrix[0][c] = 0;
      }
      addStep("First row is now zero.", 21, { r: 0 });
    }

    if (isCol0) {
      addStep("First column was marked, setting it to 0.", 22, { c: 0 });
      for (let r = 0; r < R; r++) {
        matrix[r][0] = 0;
      }
      addStep("First column is now zero.", 23, { c: 0 });
    }

    addStep("Algorithm complete. Final matrix shown.", -1, {}, [], true);
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(matrix);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [matrix]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setMatrix(testCases[index].matrix);
  };

  const addCustomTestCase = () => {
    try {
      const parsedMatrix = JSON.parse(customInput);
      if (
        !Array.isArray(parsedMatrix) ||
        !parsedMatrix.every(
          (row) =>
            Array.isArray(row) && row.every((cell) => typeof cell === "number")
        )
      ) {
        throw new Error("Invalid format.");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 5}`,
        matrix: parsedMatrix,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid format. Use a JSON 2D array like [[1,0],[2,3]]");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Grid className="text-blue-500" size={24} /> Set Matrix Zeroes
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a matrix, if an element is 0, set its entire row and
                column to 0.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">
                      {tc.matrix
                        .map((row, i) => `[${row.join(",")}]`)
                        .join(" ")}
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom matrix: [[1,0],[2,3]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>
            <div className="flex justify-center p-2">
              {currentStepData && (
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `repeat(${
                      currentStepData.matrix[0]?.length || 1
                    }, minmax(0, 1fr))`,
                  }}
                >
                  {currentStepData.matrix.map((row, r) =>
                    row.map((cell, c) => {
                      const { pointers } = currentStepData;
                      let baseClass = "bg-gray-100 border-gray-300";
                      let isPointer = pointers?.r === r && pointers?.c === c;
                      let isMark =
                        (pointers?.r_mark === r && c === 0) ||
                        (pointers?.c_mark === c && r === 0);
                      let isMarkerCell = r === 0 || c === 0;

                      if (isPointer) {
                        baseClass = "bg-blue-300 border-blue-500 scale-110";
                      } else if (isMark) {
                        baseClass = "bg-yellow-300 border-yellow-500";
                      } else if (isMarkerCell) {
                        baseClass = "bg-gray-200 border-gray-400";
                      }

                      return (
                        <div
                          key={`${r}-${c}`}
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm font-bold ${baseClass}`}
                        >
                          {cell}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Optimal Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal strategy avoids using extra arrays by repurposing the
              first row and first column of the matrix itself as markers. A
              separate variable is used to track the state of the first column
              to prevent conflicts with the first row's marker.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time Complexity:</span> O(M * N)
              </div>
              <div>
                <span className="font-medium">Space Complexity:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Using the first row/col as a "memento" or "marker" is a common
                  pattern for in-place matrix problems.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The state of `matrix[0][0]` is overloaded; it marks both the
                  first row and first column. A separate variable (`isCol0`) is
                  needed to disambiguate.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The matrix must be processed in a specific order: 1. Mark
                  headers. 2. Update inner matrix. 3. Update headers. Reversing
                  this order would corrupt the markers.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 border-2 border-gray-400 rounded"></div>
                <span>Marker Row/Column</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-300 border-2 border-blue-500 rounded"></div>
                <span>Current cell being checked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-300 border-2 border-yellow-500 rounded"></div>
                <span>Marker being set to 0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetMatrixZeroesVisualizer;
